<!-- eslint-disable no-irregular-whitespace -->
<template>
  <Teleport to="body">
    <Transition
      enter-active-class="motion-safe:transition-opacity motion-safe:duration-300"
      enter-from-class="opacity-0"
      leave-active-class="motion-safe:transition-opacity motion-safe:duration-200"
      leave-to-class="opacity-0"
    >
      <RevBackdrop
        v-if="isOpen && !isRemoved"
        class="z-[4] hidden md:block"
        @click="isOpen = false"
      />
    </Transition>

    <div
      v-if="!isRemoved"
      class="fixed bottom-0 left-0 right-0 z-[5] md:bottom-12 md:left-auto md:right-12 md:w-[375px]"
    >
      <div
        :class="{ 'transition-transform': !isDragging }"
        :style="{
          transform: `translateY(${dragOffset}px)`,
        }"
      >
        <div
          class="bg-surface-default-mid rounded-t-lg w-full overflow-hidden md:rounded-lg"
        >
          <!-- Header - Draggable on mobile, clickable on desktop -->
          <RevButtonBase
            class="w-full md:cursor-pointer"
            role="button"
            :style="{ backgroundColor: headerBackgroundColor }"
            :tabindex="0"
            @click="onHeaderClick"
            @touchend="onTouchEnd"
            @touchmove="onTouchMove"
            @touchstart="onTouchStart"
          >
            <!-- Drag Handle (Mobile only) -->
            <div class="flex justify-center pb-4 pt-8 md:hidden">
              <div
                class="border-static-default-low rounded-full h-4 w-72 border-2"
              />
            </div>

            <div
              :class="[
                headerTextColor,
                'flex w-full items-start gap-8 p-16 text-left',
              ]"
            >
              <div class="shrink-0">
                <slot name="logo" />
              </div>

              <div class="grow">
                <h2 class="body-1-bold flex-1">
                  <slot name="title" /> 
                  <RevLink :underlined="false">
                    <span :class="['underline md:hidden', headerTextColor]">
                      {{ i18n(translations.moreInfoLink) }}
                    </span>
                  </RevLink>
                </h2>

                <div
                  class="hidden overflow-hidden motion-safe:transition-[max-height,opacity] motion-safe:duration-200 md:block"
                  :class="
                    isOpen
                      ? 'pointer-events-none max-h-0 opacity-0'
                      : 'max-h-24 opacity-100 mt-16'
                  "
                >
                  <RevLink>
                    <span :class="[headerTextColor, 'body-2-link']">
                      {{ i18n(translations.moreInfoLink) }}
                    </span>
                  </RevLink>
                </div>
              </div>

              <RevButtonBase @click.stop="isRemoved = true">
                <IconCross
                  class="shrink-0"
                  :name="isOpen ? 'chevron-down' : 'chevron-up'"
                  size="24"
                />
              </RevButtonBase>
            </div>
          </RevButtonBase>

          <!-- Content - Collapsible -->
          <Transition
            enter-active-class="motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-300 motion-safe:ease-out"
            enter-from-class="grid-rows-[0fr] opacity-0"
            enter-to-class="grid-rows-[1fr] opacity-100"
            leave-active-class="motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-200 motion-safe:ease-in"
            leave-from-class="grid-rows-[1fr] opacity-100"
            leave-to-class="grid-rows-[0fr] opacity-0"
          >
            <div v-if="isOpen" class="grid">
              <div class="overflow-hidden">
                <div class="space-y-24 p-24">
                  <slot name="body" />
                </div>
              </div>
            </div>
          </Transition>
        </div>
        <div
          class="absolute bottom-[-99.9vh] h-[100vh] w-full md:hidden"
          :class="isOpen ? 'bg-surface-default-mid' : ''"
          :style="!isOpen ? { backgroundColor: headerBackgroundColor } : {}"
        ></div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevBackdrop } from '@ds/components/Backdrop'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevLink } from '@ds/components/Link'
import { IconCross } from '@ds/icons/IconCross'

import translations from './Popup.translations'

defineProps<{
  headerBackgroundColor: string
  headerTextColor: 'text-static-light-hi' | 'text-static-default-hi'
}>()
const i18n = useI18n()
const { trackClick } = useTracking()

const isOpen = ref(false)
const isRemoved = ref(false)

// Drag functionality (mobile only)
const dragOffset = ref(0)
const startY = ref(0)
const isDragging = ref(false)

function onHeaderClick() {
  trackClick({
    zone: 'telco_info_block',
    name: !isOpen.value ? 'open_block' : 'close_block',
  })
  isOpen.value = !isOpen.value
}

function onTouchStart(e: TouchEvent) {
  startY.value = e.touches[0].clientY
  isDragging.value = true
}

function onTouchMove(e: TouchEvent) {
  if (!isDragging.value) return

  // Prevent page scroll while dragging
  e.preventDefault()

  const currentY = e.touches[0].clientY
  const diff = currentY - startY.value

  // Apply elastic resistance - only 30% of finger movement
  const MAX_DRAG = 300
  const resistance = 0.3
  dragOffset.value = Math.max(-MAX_DRAG, Math.min(MAX_DRAG, diff * resistance))
}

function onTouchEnd() {
  if (!isDragging.value) {
    dragOffset.value = 0

    return
  }

  const THRESHOLD = 30

  // If dragged up enough and closed -> open
  if (dragOffset.value < -THRESHOLD && !isOpen.value) {
    isOpen.value = true
  }
  // If dragged down enough and open -> close
  else if (dragOffset.value > THRESHOLD && isOpen.value) {
    isOpen.value = false
  }

  // Reset
  dragOffset.value = 0
  isDragging.value = false
}
</script>
